import { useState } from 'react';
import type { SellerDebtRow } from '@chicare/shared';

import { useRecordDebtPayment, useSellerDebt, useSellerDebts } from '@/lib/adminQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * The collections desk.
 *
 * ── WHY A PHONE NUMBER IS ON EVERY ROW ──────────────────────────────────────
 *
 * Automatic recovery takes a slice of a pharmacy's next orders and needs nobody
 * to do anything — which makes it useless against the pharmacy you most want to
 * collect from, the one who has stopped selling. For them the only thing that
 * works is a person ringing up. So the number to ring is on the row, not two
 * clicks away.
 *
 * ── WHY OLDEST FIRST, NOT LARGEST ───────────────────────────────────────────
 *
 * A small debt at fifty days is closer to being written off than a large one at
 * three. Age is what predicts loss here, so age is the sort.
 */
export function CollectionsScreen() {
  const [status, setStatus] = useState('open');
  const [selected, setSelected] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch } = useSellerDebts(status);

  if (isError) {
    return (
      <AdminError title="We couldn't load outstanding debts" error={error} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Collections</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
          Money owed after a buyer was refunded post-settlement. It comes back automatically from
          a pharmacy&rsquo;s next orders — but a pharmacy that has stopped selling has no next
          orders, and those are the ones on this screen.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            ['open', 'Outstanding'],
            ['recovered', 'Settled'],
            ['written_off', 'Written off'],
            ['all', 'All'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setStatus(key);
              setSelected(null);
            }}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}

        {data ? (
          <span className="ml-auto font-mono text-[11.5px] text-faint">
            {data.meta.count} account{data.meta.count === 1 ? '' : 's'} ·{' '}
            <b className="text-ink">{data.meta.outstanding_display}</b> owed
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" radius="card" />
          <Skeleton className="h-24 w-full" radius="card" />
        </div>
      ) : data && data.debts.length === 0 ? (
        <EmptyState
          title="Nothing outstanding"
          body={
            status === 'open'
              ? 'No pharmacy owes ChiCare money right now.'
              : 'Nothing to show for this filter.'
          }
          action={
            status === 'all' ? (
              <Button variant="ghost" icon="refresh" onClick={() => refetch()}>
                Check again
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setStatus('all')}>
                See every account
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-3">
          {data?.debts.map((debt) => (
            <DebtCard
              key={debt.id}
              debt={debt}
              open={selected === debt.pharmacy.id}
              onToggle={() => setSelected(selected === debt.pharmacy.id ? null : debt.pharmacy.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DebtCard({
  debt,
  open,
  onToggle,
}: {
  debt: SellerDebtRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className={cn('overflow-hidden p-0', open && 'ring-1 ring-blue')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-col gap-3 p-4 text-left sm:flex-row sm:items-center"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-bold text-ink">
              {debt.pharmacy.name ?? 'Unknown pharmacy'}
            </span>
            <StageBadge stage={debt.escalation_stage} status={debt.status} />
          </div>

          <p className="m-0 mt-1.5 text-[12.5px] text-muted">
            Raised {debt.age_days} day{debt.age_days === 1 ? '' : 's'} ago
            {debt.order ? (
              <>
                {' '}
                · <span className="font-mono text-[12px]">{debt.order}</span>
              </>
            ) : null}
            {debt.reason ? ` · ${debt.reason.replace(/_/g, ' ')}` : null}
          </p>
        </div>

        <div className="flex items-center gap-4 sm:flex-col sm:items-end sm:gap-0.5">
          <span className="font-mono text-[15px] font-bold tabular-nums text-ink">
            {debt.outstanding_display}
          </span>
          {debt.pharmacy.phone ? (
            <a
              href={`tel:${debt.pharmacy.phone}`}
              onClick={(event) => event.stopPropagation()}
              className="inline-flex min-h-11 items-center gap-1 text-[12px] font-medium text-blue no-underline sm:min-h-0"
            >
              <Icon name="phone" size={12} />
              {debt.pharmacy.phone}
            </a>
          ) : null}
        </div>
      </button>

      {open ? <DebtDetail pharmacyId={debt.pharmacy.id} outstandingKobo={debt.outstanding_kobo} /> : null}
    </Card>
  );
}

function DebtDetail({
  pharmacyId,
  outstandingKobo,
}: {
  pharmacyId: number;
  outstandingKobo: number;
}) {
  const { data, isLoading, isError, error, refetch } = useSellerDebt(pharmacyId);

  if (isError) {
    return (
      <div className="border-t border-line p-4">
        <AdminError title="We couldn't load this account" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 border-t border-line p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="border-t border-line bg-paper p-4">
      <section className="mb-4">
        <h3 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
          Who to call
        </h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-[12.5px] sm:grid-cols-2">
          <Fact label="Contact" value={data.pharmacy.contact_name ?? '—'} />
          <Fact label="Phone" value={data.pharmacy.contact_phone ?? '—'} mono />
          <Fact label="Email" value={data.pharmacy.contact_email ?? '—'} />
          <Fact
            label="Currently"
            value={data.pharmacy.can_sell ? 'Selling normally' : 'Listings paused'}
          />
        </dl>
      </section>

      {/* §4.11: every deduction links to the refund that caused it. A silent
          deduction destroys more trust than the money recovers. */}
      <section className="mb-4">
        <h3 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
          What this is for
        </h3>
        <div className="w-full overflow-x-auto rounded-card border border-line bg-white">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                {['Raised', 'Order', 'Reason', 'Owed', 'Recovered', 'Left'].map((header, index) => (
                  <th
                    key={header}
                    className={cn(
                      'whitespace-nowrap border-b border-line px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-faint',
                      index > 2 ? 'text-right' : 'text-left',
                    )}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.statement.lines.map((line, index) => (
                <tr key={`${line.order ?? 'line'}-${index}`}>
                  <td className="whitespace-nowrap border-b border-line px-3 py-2 text-muted">
                    {line.raised_on ?? '—'}
                  </td>
                  <td className="whitespace-nowrap border-b border-line px-3 py-2 font-mono text-[11.5px] text-ink">
                    {line.order ?? '—'}
                  </td>
                  <td className="border-b border-line px-3 py-2 text-muted">
                    {line.reason?.replace(/_/g, ' ') ?? '—'}
                  </td>
                  <td className="whitespace-nowrap border-b border-line px-3 py-2 text-right font-mono tabular-nums text-ink">
                    {line.amount_display}
                  </td>
                  <td className="whitespace-nowrap border-b border-line px-3 py-2 text-right font-mono tabular-nums text-muted">
                    {line.recovered_display}
                  </td>
                  <td className="whitespace-nowrap border-b border-line px-3 py-2 text-right font-mono font-bold tabular-nums text-ink">
                    {line.outstanding_display}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {outstandingKobo > 0 ? (
        <PaymentPanel pharmacyId={pharmacyId} outstandingKobo={outstandingKobo} />
      ) : null}
    </div>
  );
}

/**
 * Log a transfer that has landed.
 *
 * Nothing physically arrives when this is submitted — the person pressing it is
 * ASSERTING money reached the bank. The reference is what makes that assertion
 * checkable against a statement six months later, so it is required here and
 * required again by the API, which refuses rather than trusting this screen.
 */
function PaymentPanel({
  pharmacyId,
  outstandingKobo,
}: {
  pharmacyId: number;
  outstandingKobo: number;
}) {
  const record = useRecordDebtPayment();

  const [naira, setNaira] = useState(() => String(Math.round(outstandingKobo / 100)));
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const amountKobo = Math.round(Number(naira || 0) * 100);
  const tooMuch = amountKobo > outstandingKobo;
  const valid = amountKobo > 0 && !tooMuch && reference.trim().length >= 4;

  if (record.isSuccess) {
    return (
      <div
        data-testid="payment-recorded"
        className="rounded-card border border-verified bg-verified-tint p-3.5"
      >
        <p className="m-0 text-[13px] font-bold text-verified-ink">
          {record.data.result.applied_display} recorded
        </p>
        <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-ink">{record.data.result.note}</p>
      </div>
    );
  }

  return (
    <section className="rounded-card border border-line bg-white p-3.5">
      <h3 className="m-0 mb-2.5 text-[13px] font-bold text-ink">Log a payment</h3>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Amount received (₦)</span>
          <input
            inputMode="decimal"
            value={naira}
            onChange={(event) => setNaira(event.target.value.replace(/[^\d.]/g, ''))}
            className="min-h-11 w-full rounded-btn border border-line bg-paper px-3 font-mono text-[13.5px] tabular-nums text-ink outline-none focus:border-blue"
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Bank reference</span>
          <input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="GTB/TRF/88213"
            className="min-h-11 w-full rounded-btn border border-line bg-paper px-3 font-mono text-[13.5px] text-ink outline-none focus:border-blue"
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">Note (optional)</span>
        <input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Spoke to Mrs Okafor, transfer sent same morning."
          className="min-h-11 w-full rounded-btn border border-line bg-paper px-3 text-[13.5px] text-ink outline-none focus:border-blue"
        />
      </label>

      {tooMuch ? (
        <p role="alert" className="mt-2 text-[12.5px] font-medium text-danger">
          That is more than is outstanding. Log the exact amount owed and handle any surplus
          separately — ChiCare should not sit on a pharmacy&rsquo;s money.
        </p>
      ) : null}

      {record.error ? (
        <p role="alert" className="mt-2 text-[12.5px] font-medium text-danger">
          {record.error instanceof Error ? record.error.message : 'That did not save.'}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          disabled={!valid || record.isPending}
          onClick={() =>
            record.mutate({
              pharmacyId,
              amountKobo,
              reference: reference.trim(),
              note: note.trim() || undefined,
              idempotencyKey,
            })
          }
        >
          {record.isPending ? 'Saving…' : 'Record payment'}
        </Button>

        <p className="m-0 max-w-[46ch] text-[11.5px] leading-relaxed text-muted">
          This is audit-logged against your account. If it clears the balance, their listings come
          back automatically and they are told.
        </p>
      </div>
    </section>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted">{label}</dt>
      <dd className={cn('m-0 font-medium text-ink', mono && 'font-mono')}>{value}</dd>
    </div>
  );
}

/**
 * Where on the chase ladder this account has reached — so whoever picks up the
 * phone knows what the pharmacy has already been told before they say it again.
 */
function StageBadge({ stage, status }: { stage: number; status: string }) {
  if (status === 'recovered') return <Badge tone="verified">Settled</Badge>;
  if (status === 'written_off') return <Badge tone="danger">Written off</Badge>;

  const labels: Record<number, { tone: 'neutral' | 'caution' | 'danger'; label: string }> = {
    0: { tone: 'neutral', label: 'Not yet chased' },
    1: { tone: 'neutral', label: 'Reminder sent' },
    2: { tone: 'caution', label: 'Final notice sent' },
    3: { tone: 'danger', label: 'Listings paused' },
    4: { tone: 'danger', label: 'Written off' },
  };

  const { tone, label } = labels[stage] ?? { tone: 'neutral' as const, label: 'Not yet chased' };

  return <Badge tone={tone}>{label}</Badge>;
}
